import { PropsWithChildren } from 'react';

import { SuccessPaymentModal } from '@/features/success-payment-modal';
import { DashboardWrongModalContent } from '@/features/wrong-modal';

import { ArrowLeft } from '@/shared/assets';
import { DashboardDialogProps, Heading, Typography } from '@/shared/ui';

import { SendMoneySteps } from '../../../model/types';
import { SendMoneyStore } from '../../../model/useSendMoney';
import { BankStepForm } from '../send-money-shared/ui/bank-step';
import { RecipientStepForm } from '../send-money-shared/ui/recipient-step';
import { ReviewDetails } from '../send-money-shared/ui/review-details';
import { SendMoneyAmount } from '../send-money-shared/ui/send-money-amount';
import { InfoStepForm } from './info-step-form';

interface SendMoneySomeoneProps extends PropsWithChildren, SendMoneyStore {
  onDone?: VoidFunction;
  onBack: () => void;
  isFirst?: boolean;
}

export const getSendMoneySomeoneProps = ({
  onDone,
  step,
  setStep,
  amount,
  data,
  resetData,
  setAmount,
  onBack,
  isFirst,
}: SendMoneySomeoneProps) => {
  const handleClose = () => {
    if (step === 'success') {
      setStep('info');
      resetData();
      onDone?.();
    }
    if (step === 'info') {
      resetData();
    }
    if (step === 'amount') {
      setAmount('');
    }
  };
  const sendMoneySomeoneFlow: Record<SendMoneySteps, DashboardDialogProps> = {
    info: {
      title: 'send money',
      content: <InfoStepForm />,
      subtitle: 'To personal account',
      subtitleProps: { className: 'text-base-100 leading-6' },
      animationProps: {
        id: 'send-money',
        isFirst: true,
      },
      scrollToTopId: 'send-money-id',
      disableOutsideClick: !isFirst,
      serviceButton: !isFirst
        ? {
            icon: <ArrowLeft className="min-h-6 min-w-6" />,
            onClick: () => {
              onBack();
            },
          }
        : undefined,
      hideCloseButton: !isFirst,
    },
    bank: {
      title: "bank's information",
      content: <BankStepForm />,
      scrollToTopId: 'bank-info-id',
      disableOutsideClick: true,
      serviceButton: {
        icon: <ArrowLeft className="!h-6 !w-6" />,
        onClick: () => {
          setStep('info');
        },
      },
      hideCloseButton: true,
    },
    address: {
      title: "recipient's details",
      content: <RecipientStepForm />,
      disableOutsideClick: true,
      scrollToTopId: 'recipient-details-id',
      serviceButton: {
        icon: <ArrowLeft className="!h-6 !w-6" />,
        onClick: () => {
          setStep('bank');
        },
      },
      hideCloseButton: true,
    },
    amount: {
      title: 'send money',
      content: <SendMoneyAmount />,
      height: 'fixed',
      scrollToTopId: 'send-money-amount-id',
      animationProps: {
        id: 'send-money-amount',
      },

      serviceButton: {
        icon: <ArrowLeft className="!h-6 !w-6" />,
        onClick: () => {
          setStep('info');
        },
      },
      hideCloseButton: true,
    },
    success: {
      hideCloseButton: true,
      scrollToTopId: 'success-payment-id',
      content: (
        <SuccessPaymentModal
          id={data?.id}
          onDone={handleClose}
          hasSwitch={!!data?.id}
          mainContent={
            <div className="flex flex-col gap-4 text-center">
              <Heading variant="l">
                - {data?.account?.symbol} {data?.totalAmount}
              </Heading>
              <Typography variant="16-r">
                <p>Money is on its way to</p>
                <p>
                  {data?.firstName} {data?.lastName}
                </p>
              </Typography>
            </div>
          }
        />
      ),
      animationProps: {
        id: 'success',
      },
    },
    'review-details': {
      title: 'review details',
      scrollToTopId: 'review-details-id',
      content: <ReviewDetails />,
      animationProps: {
        id: 'review-details',
      },
      height: 'fixed',
      hideCloseButton: true,
      serviceButton: {
        icon: <ArrowLeft className="!h-6 !w-6" />,
        onClick: () => {
          setStep('amount');
        },
      },
    },
    error: {
      headerProps: { className: 'min-h-[60px]' },
      scrollToTopId: 'error-id',
      content: <DashboardWrongModalContent onClose={() => null} />,
      serviceButton: {
        icon: <ArrowLeft className="!h-6 !w-6" />,
        onClick: () => {
          setStep('info');
        },
      },
      animationProps: {
        id: 'error',
      },
    },
  };

  return sendMoneySomeoneFlow[step];
};
